import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { queueCount, useAdminActions } from '../context/AdminActionsContext';
import ActionCount from './ActionCount';

export default function AdminActionBell() {
  const { summary, loading, error, refresh } = useAdminActions();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const queues = summary?.queues || [];
  const total = queueCount(queues);
  const unknown = error || !summary || total.unavailable;
  useEffect(() => { setOpen(false); }, [location.pathname, location.search]);
  useEffect(() => {
    if (!open) return;
    panel.current?.focus();
    const outside = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setOpen(false); trigger.current?.focus(); }
    };
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  return <div ref={root} className="relative" onBlur={event => {
    if (!event.currentTarget.contains(event.relatedTarget as Node)) setOpen(false);
  }}>
    <button ref={trigger} type="button" onClick={() => setOpen(value => !value)}
      aria-expanded={open} aria-controls="admin-actions-panel" aria-label="Pending admin actions"
      className="flex items-center gap-2 rounded-lg border border-primary/15 px-3 py-2 text-sm font-semibold text-primary hover:bg-primary/5">
      <span aria-hidden="true">🔔</span><span className="hidden xl:inline">Actions</span>
      <ActionCount count={total.count} unknown={unknown} />
    </button>
    {open && <div ref={panel} id="admin-actions-panel" role="region" aria-label="Admin action queues" tabIndex={-1}
      className="fixed right-4 top-20 w-96 max-w-[calc(100vw-272px)] rounded-xl border border-gray-200 bg-white shadow-xl outline-none">
      <div className="flex items-center justify-between border-b p-4">
        <div><h3 className="font-bold text-gray-800">Pending admin actions</h3>
          <p className="text-xs text-gray-500">Open a queue to review submissions.</p></div>
        <button type="button" onClick={refresh} disabled={loading} className="text-xs font-semibold text-primary disabled:opacity-50">Refresh</button>
      </div>
      <div className="max-h-[65vh] overflow-y-auto p-3">
        {loading && !summary && <p role="status" className="p-2 text-sm text-gray-500">Loading counts…</p>}
        {error && <p role="alert" className="mb-2 rounded bg-amber-50 p-2 text-sm text-amber-900">
          Counts could not refresh.{summary ? ' Showing the last known counts.' : ' Try Refresh.'}
        </p>}
        {summary && !error && !total.unavailable && total.count === 0 && <p className="p-2 text-sm text-green-800">No pending actions in the tracked queues.</p>}
        {queues.filter(queue => queue.status === 'unavailable' || (queue.count ?? 0) > 0).map(queue =>
          <Link key={queue.key} to={queue.path} className="mb-1 flex items-center justify-between gap-3 rounded-lg px-3 py-3 text-sm text-gray-700 hover:bg-cream">
            <span>{queue.label}</span>
            {queue.status === 'unavailable' ? <span className="text-xs text-amber-800">Unavailable</span> : <ActionCount count={queue.count ?? 0} unknown={error} />}
          </Link>)}
      </div>
      <div className="border-t px-4 py-3 text-xs text-gray-500">
        {summary ? `Last updated ${new Date(summary.generated_at).toLocaleTimeString()}. ` : ''}
        Updates every minute while this panel is visible. Counts are queue entries, not unique users.
      </div>
    </div>}
  </div>;
}
